/**
 * Shared weekday-name formatting for the ai-assistant widgets. Used by both WidgetObject.tsx
 * (current-value display) and the widget-approval views (WindowRangesView's weekday dropdown) so
 * a weekday restriction reads identically — and is properly localized — regardless of which
 * widget renders it.
 *
 * Previously WidgetApproval.tsx had its OWN copy of this mapping (`WEEKDAY_FULL`), hardcoded to
 * Spanish day names regardless of the active UI locale. WidgetObject.tsx's version was already
 * correctly localized via Intl.DateTimeFormat. This file keeps the correct (localized) approach
 * as the single implementation, fixing that inconsistency.
 *
 * Maps the backend weekday enum to a reference date in a week that starts on Monday
 * (2024-01-08 is a Monday), so we can render localized day names via Intl without maintaining a
 * translation entry per day. Order also defines the display order.
 */
const WEEKDAY_REFERENCE: Array<{ key: string; date: Date }> = [
  { key: "MONDAY", date: new Date(2024, 0, 8) },
  { key: "TUESDAY", date: new Date(2024, 0, 9) },
  { key: "WEDNESDAY", date: new Date(2024, 0, 10) },
  { key: "THURSDAY", date: new Date(2024, 0, 11) },
  { key: "FRIDAY", date: new Date(2024, 0, 12) },
  { key: "SATURDAY", date: new Date(2024, 0, 13) },
  { key: "SUNDAY", date: new Date(2024, 0, 14) },
];

// Intl.DateTimeFormat instances are somewhat expensive to construct and a given locale is reused
// across every row/window/promotion in a render pass — cache one per locale instead of
// constructing a fresh formatter on every call (previously done inline in formatWeekDaysField).
const weekdayFormatterCache = new Map<string, Intl.DateTimeFormat>();

function getWeekdayFormatter(locale: string): Intl.DateTimeFormat {
  const key = locale || "en";
  let fmt = weekdayFormatterCache.get(key);
  if (!fmt) {
    fmt = new Intl.DateTimeFormat(key, { weekday: "long" });
    weekdayFormatterCache.set(key, fmt);
  }
  return fmt;
}

/** Localized, capitalized full names for the given backend weekday codes, in Mon…Sun order. */
export function weekdayNames(codes: string[], locale: string): string[] {
  const selected = new Set(codes.map(v => String(v).toUpperCase()));
  const fmt = getWeekdayFormatter(locale);
  return WEEKDAY_REFERENCE.filter(({ key }) => selected.has(key)).map(({ date }) => {
    const name = fmt.format(date);
    // Capitalize each day (some locales lowercase weekday names).
    return name.charAt(0).toUpperCase() + name.slice(1);
  });
}

/** Same as `weekdayNames`, joined into a single comma-separated string (or "—" when empty). */
export function formatWeekDaysField(value: unknown, locale: string): string {
  if (!Array.isArray(value) || value.length === 0) return "—";
  const names = weekdayNames(value.map(String), locale);
  return names.length > 0 ? names.join(", ") : "—";
}

/** All seven weekday codes in Mon…Sun order — used when a range applies every day. */
export function allWeekdayCodes(): string[] {
  return WEEKDAY_REFERENCE.map(({ key }) => key);
}
